/**
 * DrawingCanvas — full interactive drawing surface for the SCOPE path.
 *
 * Supports 3 tool modes (driven by parent):
 *   - 'pan'       : drag to pan, wheel to zoom. No drawing.
 *   - 'calibrate' : two clicks → emits onCalibratePoints([p1, p2])
 *   - 'draw'      : click to add point to current polygon. If clicking near start
 *                   (SNAP_PX in screen space) → emits onCloseRoom. Emits onAddPoint
 *                   for each intermediate click.
 *
 * All coordinates stored in IMAGE-SPACE pixels (independent of zoom/pan) so that
 * polygons render consistently regardless of viewport scale.
 *
 * Props:
 *   imageUrl / imageWidth / imageHeight   — the uploaded plan
 *   rooms            — completed rooms   [{ id, name, polygon_px, area_m2 }]
 *   currentPolygon   — in-progress polygon points [[x,y], ...] (image-space)
 *   calibrationLine  — during calibration: [[x,y]] or [[x,y],[x,y]] or null
 *   scale            — { factor_m_per_px } | null, used only for live overlay labels
 *   mode             — 'pan' | 'calibrate' | 'draw'
 *   onAddPoint(pt)        — called when user clicks in draw mode (image-space pt)
 *   onCalibratePoints(pts)— called once both calibration pts are set
 *   onCloseRoom()         — user clicked near start to close polygon (≥3 pts)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Image as KImage, Line, Text, Group, Rect, Circle } from 'react-konva';
import { polygonAreaPx, polygonPerimeterPx, pxAreaToM2, pxLenToM, flattenPoints, dist } from './geometry';

const ZOOM_MIN = 0.15;
const ZOOM_MAX = 6;
const ZOOM_STEP = 1.12;
const SNAP_PX_SCREEN = 12;  // snap-close tolerance in screen pixels
const LONG_PRESS_MS = 350;
const LONG_PRESS_MOVE_TOLERANCE = 8;  // px of movement that still counts as a "press"
const MAGNIFIER_SIZE = 140;  // diameter of the magnifier overlay
const MAGNIFIER_ZOOM = 3;
const MAGNIFIER_OFFSET = { x: -90, y: -130 };  // position relative to finger (above-left)

function useHtmlImage(src) {
  const [img, setImg] = useState(null);
  useEffect(() => {
    if (!src) { setImg(null); return; }
    const el = new window.Image();
    // Note: no crossOrigin — S3 presigned URLs don't guarantee CORS headers,
    // and we don't read pixel data from the canvas (no toDataURL calls).
    el.src = src;
    const onLoad = () => setImg(el);
    el.addEventListener('load', onLoad);
    return () => el.removeEventListener('load', onLoad);
  }, [src]);
  return img;
}

export const DrawingCanvas = ({
  imageUrl,
  imageWidth,
  imageHeight,
  rooms = [],
  currentPolygon = [],
  calibrationLine = null,
  scale = null,
  mode = 'pan',
  onAddPoint,
  onCalibratePoints,
  onCloseRoom,
  containerWidth = 1100,
  containerHeight = 720,
}) => {
  const stageRef = useRef(null);
  const planImg = useHtmlImage(imageUrl);

  // Fit-to-viewport initial zoom
  const fitScale = useMemo(() => {
    if (!imageWidth || !imageHeight) return 1;
    const sx = (containerWidth - 40) / imageWidth;
    const sy = (containerHeight - 40) / imageHeight;
    return Math.min(sx, sy, 1);
  }, [imageWidth, imageHeight, containerWidth, containerHeight]);

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [hover, setHover] = useState(null);  // image-space hover point when drawing
  const [pendingCalib, setPendingCalib] = useState(null);  // first point while calibrating

  // ── Touch / mobile state ───────────────────────────────────────────
  // Magnifier appears after 350ms press-and-hold on a single finger
  //   { screen: {x,y},        // touch position on screen (relative to container)
  //     image: {x,y},         // corresponding image-space coord
  //     startScreen: {x,y} }  // the original touch anchor, for movement tolerance
  const [magnifier, setMagnifier] = useState(null);
  const containerRef = useRef(null);
  const magCanvasRef = useRef(null);
  const longPressTimer = useRef(null);
  // Guard — set briefly after a magnifier commit so Konva's follow-up onTap
  // (synthesised from the same touch) doesn't double-commit a point.
  const suppressNextClickRef = useRef(false);
  const touchStateRef = useRef({
    activeTouches: new Map(), // touchId → {x, y}
    initialPinchDist: 0,
    initialPinchZoom: 1,
    initialPinchCenter: { x: 0, y: 0 },
    initialPan: { x: 0, y: 0 },
    pressStart: null,  // {x, y, time} for single-touch long-press detection
  });

  // Reset to fit when the image changes
  useEffect(() => {
    setZoom(fitScale);
    setPan({
      x: (containerWidth - imageWidth * fitScale) / 2,
      y: (containerHeight - imageHeight * fitScale) / 2,
    });
  }, [fitScale, imageWidth, imageHeight, containerWidth, containerHeight]);

  // Reset pending calibration when mode changes
  useEffect(() => {
    setPendingCalib(null);
    if (mode !== 'draw') setHover(null);
  }, [mode]);

  // ── Middle-click drag to pan (works in all modes) ──
  // Captures mousedown on button===1, listens for mousemove/up on window
  // so a fast drag that leaves the container doesn't get stuck.
  // Also suppresses the browser's auto-scroll cursor AND the context menu
  // that some browsers emit for middle-click on certain platforms.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let isMiddleDragging = false;
    let lastX = 0;
    let lastY = 0;
    const onMouseDown = (e) => {
      if (e.button !== 1) return;  // middle button only
      e.preventDefault();
      e.stopPropagation();
      isMiddleDragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
      container.style.cursor = 'grabbing';
    };
    const onMouseMove = (e) => {
      if (!isMiddleDragging) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
    };
    const onMouseUp = (e) => {
      if (!isMiddleDragging) return;
      // Middle button up → end drag. Also swallow any synthetic context menu
      // that follows on certain browsers (Firefox on Linux).
      if (e && e.button === 1) { e.preventDefault(); e.stopPropagation(); }
      isMiddleDragging = false;
      container.style.cursor = '';
    };
    // Suppress the browser's default auto-scroll/pan cursor on middle-click
    const onAuxClick = (e) => { if (e.button === 1) { e.preventDefault(); e.stopPropagation(); } };
    // Suppress the context menu emitted by some browsers for middle-click
    const onContextMenu = (e) => {
      // Only suppress middle-button-initiated context menus — keep right-click menu available
      // (Chromium sends button=1 in contextmenu for middle-click; Firefox sends button=0 wrongly,
      // but fires aux-click first so the flag is set)
      if (e.button === 1 || isMiddleDragging) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    container.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    container.addEventListener('auxclick', onAuxClick);
    container.addEventListener('contextmenu', onContextMenu);
    return () => {
      container.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      container.removeEventListener('auxclick', onAuxClick);
      container.removeEventListener('contextmenu', onContextMenu);
    };
  }, []);

  const screenToImage = (sx, sy) => [
    (sx - pan.x) / zoom,
    (sy - pan.y) / zoom,
  ];

  const imageToScreen = ([x, y]) => [
    x * zoom + pan.x,
    y * zoom + pan.y,
  ];

  const handleWheel = (e) => {
    e.evt.preventDefault();
    const stage = stageRef.current;
    if (!stage) return;
    const pointer = stage.getPointerPosition();
    const oldScale = zoom;
    const mousePointTo = {
      x: (pointer.x - pan.x) / oldScale,
      y: (pointer.y - pan.y) / oldScale,
    };
    const direction = e.evt.deltaY > 0 ? -1 : 1;
    const next = direction > 0 ? oldScale * ZOOM_STEP : oldScale / ZOOM_STEP;
    const clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, next));
    setZoom(clamped);
    setPan({
      x: pointer.x - mousePointTo.x * clamped,
      y: pointer.y - mousePointTo.y * clamped,
    });
  };

  const handleStageClick = (e) => {
    // If a magnifier just committed a point, Konva may synthesise a click/tap
    // from the same touch — swallow it.
    if (suppressNextClickRef.current) return;
    // Only react to clicks on the stage/image, not on Room groups which stop propagation
    const stage = stageRef.current;
    if (!stage) return;
    const p = stage.getPointerPosition();
    if (!p) return;
    const imgPt = screenToImage(p.x, p.y);
    // Clamp to image bounds
    if (imgPt[0] < 0 || imgPt[1] < 0 || imgPt[0] > imageWidth || imgPt[1] > imageHeight) return;

    if (mode === 'calibrate') {
      if (!pendingCalib) {
        setPendingCalib(imgPt);
      } else {
        onCalibratePoints?.([pendingCalib, imgPt]);
        setPendingCalib(null);
      }
      return;
    }

    if (mode === 'draw') {
      // If we have >=3 points and user clicked near the start point in screen space → close
      if (currentPolygon.length >= 3) {
        const startScreen = imageToScreen(currentPolygon[0]);
        const clickScreen = [p.x, p.y];
        if (dist(startScreen, clickScreen) <= SNAP_PX_SCREEN) {
          onCloseRoom?.();
          return;
        }
      }
      onAddPoint?.(imgPt);
    }
  };

  const handleMouseMove = () => {
    const stage = stageRef.current;
    if (!stage) return;
    const p = stage.getPointerPosition();
    if (!p) return;
    if (mode === 'draw' || mode === 'calibrate') {
      setHover(screenToImage(p.x, p.y));
    }
  };

  const stageDraggable = mode === 'pan';
  const cursor = mode === 'pan' ? 'grab' : 'crosshair';

  // Live-measure display for the polygon currently being drawn
  const livePolyInfo = useMemo(() => {
    if (mode !== 'draw' || currentPolygon.length < 2) return null;
    const closedPoints = currentPolygon;
    const areaPx = closedPoints.length >= 3 ? polygonAreaPx(closedPoints) : 0;
    const perimPx = polygonPerimeterPx(closedPoints);
    return {
      areaM2: scale?.factor_m_per_px ? pxAreaToM2(areaPx, scale.factor_m_per_px) : null,
      perimeterM: scale?.factor_m_per_px ? pxLenToM(perimPx, scale.factor_m_per_px) : null,
      pts: closedPoints.length,
    };
  }, [mode, currentPolygon, scale]);

  // Live-measure for the calibration line in progress
  const calibLivePx = useMemo(() => {
    if (mode !== 'calibrate' || !pendingCalib || !hover) return 0;
    return dist(pendingCalib, hover);
  }, [mode, pendingCalib, hover]);

  const snapActive = useMemo(() => {
    if (mode !== 'draw' || currentPolygon.length < 3 || !hover) return false;
    const startScreen = imageToScreen(currentPolygon[0]);
    const hoverScreen = imageToScreen(hover);
    return dist(startScreen, hoverScreen) <= SNAP_PX_SCREEN;
  }, [mode, currentPolygon, hover, zoom, pan]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Touch / mobile handlers ──────────────────────────────────────
  // Committing a point from the magnifier (touchend while visible)
  const commitMagnifierPoint = (imagePt) => {
    if (imagePt[0] < 0 || imagePt[1] < 0 || imagePt[0] > imageWidth || imagePt[1] > imageHeight) return;
    if (mode === 'calibrate') {
      if (!pendingCalib) setPendingCalib(imagePt);
      else { onCalibratePoints?.([pendingCalib, imagePt]); setPendingCalib(null); }
    } else if (mode === 'draw') {
      if (currentPolygon.length >= 3) {
        const startScreen = imageToScreen(currentPolygon[0]);
        const hoverScreen = imageToScreen(imagePt);
        if (dist(startScreen, hoverScreen) <= SNAP_PX_SCREEN) {
          onCloseRoom?.();
          return;
        }
      }
      onAddPoint?.(imagePt);
    }
  };

  // Draw the magnifier canvas content whenever magnifier position changes
  useEffect(() => {
    if (!magnifier || !planImg) return;
    const cvs = magCanvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext('2d');
    const size = MAGNIFIER_SIZE;
    cvs.width = size; cvs.height = size;
    // Circular clip
    ctx.save();
    ctx.clearRect(0, 0, size, size);
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    // Background
    ctx.fillStyle = '#F1F5F9';
    ctx.fillRect(0, 0, size, size);
    // Draw the plan, zoomed 3x, centered on the magnifier's image point
    const srcSize = size / MAGNIFIER_ZOOM;
    const sx = magnifier.image.x - srcSize / 2;
    const sy = magnifier.image.y - srcSize / 2;
    ctx.drawImage(planImg, sx, sy, srcSize, srcSize, 0, 0, size, size);

    // Also render existing room polygons as thin blue outlines so the user has context
    ctx.strokeStyle = '#2563EB';
    ctx.lineWidth = 1.5;
    for (const r of rooms || []) {
      const pts = r.polygon_px || [];
      if (pts.length < 2) continue;
      ctx.beginPath();
      for (let i = 0; i < pts.length; i++) {
        const [x, y] = pts[i];
        const mx = (x - sx) * MAGNIFIER_ZOOM;
        const my = (y - sy) * MAGNIFIER_ZOOM;
        if (i === 0) ctx.moveTo(mx, my); else ctx.lineTo(mx, my);
      }
      ctx.closePath();
      ctx.stroke();
    }

    // Render in-progress polygon
    if (currentPolygon.length > 0) {
      ctx.strokeStyle = '#2563EB';
      ctx.lineWidth = 2;
      ctx.beginPath();
      for (let i = 0; i < currentPolygon.length; i++) {
        const [x, y] = currentPolygon[i];
        const mx = (x - sx) * MAGNIFIER_ZOOM;
        const my = (y - sy) * MAGNIFIER_ZOOM;
        if (i === 0) ctx.moveTo(mx, my); else ctx.lineTo(mx, my);
      }
      ctx.stroke();
    }
    ctx.restore();

    // Crosshair at the center (where the new point will drop)
    const cx = size / 2, cy = size / 2;
    ctx.strokeStyle = '#EF4444';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(cx - 12, cy); ctx.lineTo(cx + 12, cy);
    ctx.moveTo(cx, cy - 12); ctx.lineTo(cx, cy + 12);
    ctx.stroke();
    // Crosshair circle
    ctx.beginPath();
    ctx.arc(cx, cy, 4, 0, Math.PI * 2);
    ctx.stroke();
    // White ring border for the magnifier itself
    ctx.lineWidth = 4;
    ctx.strokeStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(cx, cy, size / 2 - 2, 0, Math.PI * 2);
    ctx.stroke();
  }, [magnifier, planImg, rooms, currentPolygon]);

  // Native touch listeners (Konva's onTouch* don't give us multi-finger / raw events)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const containerRect = () => el.getBoundingClientRect();
    const getTouchScreen = (touch) => {
      const rect = containerRect();
      return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    };
    const imageFromScreen = (sx, sy) => [(sx - pan.x) / zoom, (sy - pan.y) / zoom];

    const clearLongPressTimer = () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    };

    const onTouchStart = (e) => {
      const st = touchStateRef.current;
      // Update active touches map
      for (const t of Array.from(e.touches)) {
        const p = getTouchScreen(t);
        st.activeTouches.set(t.identifier, p);
      }
      if (e.touches.length === 1) {
        // Single finger — set up long-press detection for draw/calibrate modes
        if (mode === 'draw' || mode === 'calibrate') {
          const t = e.touches[0];
          const p = getTouchScreen(t);
          st.pressStart = { x: p.x, y: p.y, time: Date.now() };
          clearLongPressTimer();
          longPressTimer.current = setTimeout(() => {
            // Still a press (not cancelled by move or second finger)
            const img = imageFromScreen(p.x, p.y);
            setMagnifier({
              screen: { x: p.x, y: p.y },
              image: { x: img[0], y: img[1] },
              startScreen: { x: p.x, y: p.y },
            });
          }, LONG_PRESS_MS);
        }
      } else if (e.touches.length === 2) {
        // Two fingers — start pinch zoom + pan
        clearLongPressTimer();
        setMagnifier(null);
        const [t1, t2] = [e.touches[0], e.touches[1]];
        const p1 = getTouchScreen(t1);
        const p2 = getTouchScreen(t2);
        st.initialPinchDist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
        st.initialPinchZoom = zoom;
        st.initialPinchCenter = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
        st.initialPan = { x: pan.x, y: pan.y };
        e.preventDefault();
      }
    };

    const onTouchMove = (e) => {
      const st = touchStateRef.current;
      // Update active touches
      for (const t of Array.from(e.touches)) {
        const p = getTouchScreen(t);
        st.activeTouches.set(t.identifier, p);
      }
      if (e.touches.length === 2) {
        // Pinch-zoom + two-finger pan
        e.preventDefault();
        const [t1, t2] = [e.touches[0], e.touches[1]];
        const p1 = getTouchScreen(t1);
        const p2 = getTouchScreen(t2);
        const currentDist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
        const currentCenter = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
        if (st.initialPinchDist > 10) {
          const scale = currentDist / st.initialPinchDist;
          const nextZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, st.initialPinchZoom * scale));
          // Keep the pinch centre locked to the same image point
          const imgAtCenter = [
            (st.initialPinchCenter.x - st.initialPan.x) / st.initialPinchZoom,
            (st.initialPinchCenter.y - st.initialPan.y) / st.initialPinchZoom,
          ];
          const newPan = {
            x: currentCenter.x - imgAtCenter[0] * nextZoom,
            y: currentCenter.y - imgAtCenter[1] * nextZoom,
          };
          setZoom(nextZoom);
          setPan(newPan);
        }
        return;
      }
      if (e.touches.length === 1) {
        const t = e.touches[0];
        const p = getTouchScreen(t);
        // If magnifier is active, update it (user is fine-tuning placement)
        if (magnifier) {
          e.preventDefault();
          const img = imageFromScreen(p.x, p.y);
          setMagnifier({
            screen: { x: p.x, y: p.y },
            image: { x: img[0], y: img[1] },
            startScreen: magnifier.startScreen,
          });
          return;
        }
        // If moved beyond tolerance, cancel long-press (user is panning or dragging away)
        if (st.pressStart) {
          const moved = Math.hypot(p.x - st.pressStart.x, p.y - st.pressStart.y);
          if (moved > LONG_PRESS_MOVE_TOLERANCE) {
            clearLongPressTimer();
            st.pressStart = null;
          }
        }
      }
    };

    const onTouchEnd = (e) => {
      const st = touchStateRef.current;
      // Remove finished touches
      const activeIds = new Set(Array.from(e.touches).map(t => t.identifier));
      for (const id of Array.from(st.activeTouches.keys())) {
        if (!activeIds.has(id)) st.activeTouches.delete(id);
      }
      if (magnifier) {
        // Commit the point at the magnifier's target (the centred crosshair)
        commitMagnifierPoint([magnifier.image.x, magnifier.image.y]);
        setMagnifier(null);
        // Stop Konva from seeing this touchend — otherwise its Stage synthesises
        // an onTap and commits a SECOND point at the release position.
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      }
      clearLongPressTimer();
      st.pressStart = null;
    };

    const onTouchCancel = () => {
      const st = touchStateRef.current;
      st.activeTouches.clear();
      clearLongPressTimer();
      st.pressStart = null;
      setMagnifier(null);
    };

    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    // Use capture: true so we receive touchend BEFORE Konva's Stage (inner canvas)
    // and can cancel the synthesised tap when the magnifier committed a point.
    el.addEventListener('touchend', onTouchEnd, { capture: true });
    el.addEventListener('touchcancel', onTouchCancel);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd, { capture: true });
      el.removeEventListener('touchcancel', onTouchCancel);
      clearLongPressTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, zoom, pan, magnifier, pendingCalib, currentPolygon, imageWidth, imageHeight]);

  return (
    <div
      ref={containerRef}
      data-testid="drawing-canvas"
      style={{
        width: containerWidth,
        height: containerHeight,
        background: '#F1F5F9',
        borderRadius: 12,
        overflow: 'hidden',
        border: '1.5px solid #E2E8F0',
        position: 'relative',
        cursor,
        touchAction: 'none',  // prevent browser scroll/pinch so we can handle gestures
        userSelect: 'none',
      }}
    >
      {!planImg && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B', fontSize: 13 }}>
          Loading plan…
        </div>
      )}
      <Stage
        ref={stageRef}
        width={containerWidth}
        height={containerHeight}
        x={pan.x}
        y={pan.y}
        scaleX={zoom}
        scaleY={zoom}
        onWheel={handleWheel}
        draggable={stageDraggable}
        onDragEnd={(e) => setPan({ x: e.target.x(), y: e.target.y() })}
        onClick={handleStageClick}
        onTap={(e) => {
          // onTap fires for touch taps. If the tap came from a touch event that we've
          // already consumed (e.g. magnifier commit), skip. Otherwise call through.
          // react-konva fires BOTH onClick and onTap on touch, so we also need to
          // dedupe: consume the tap, skip the click.
          suppressNextClickRef.current = true;
          setTimeout(() => { suppressNextClickRef.current = false; }, 300);
          handleStageClick(e);
        }}
        onMouseMove={handleMouseMove}
      >
        {/* Background — the uploaded plan */}
        <Layer listening={false}>
          {planImg && <KImage image={planImg} width={imageWidth} height={imageHeight} opacity={0.9} />}
        </Layer>

        {/* Existing rooms */}
        <Layer listening={false}>
          {rooms.map((r) => {
            const flat = flattenPoints(r.polygon_px || []);
            const pts = r.polygon_px || [];
            let cx = 0, cy = 0;
            if (pts.length) {
              for (const [x, y] of pts) { cx += x; cy += y; }
              cx /= pts.length; cy /= pts.length;
            }
            const labelW = 140 / zoom;
            const labelH = 34 / zoom;
            return (
              <Group key={r.id}>
                <Line points={flat} closed fill="rgba(37,99,235,.18)" stroke="#2563EB" strokeWidth={2 / zoom} />
                <Group x={cx - labelW / 2} y={cy - labelH / 2}>
                  <Rect width={labelW} height={labelH} cornerRadius={4 / zoom} fill="rgba(15,39,66,.92)" />
                  <Text
                    width={labelW}
                    height={labelH}
                    text={`${r.name || 'Room'}\n${r.area_m2 != null ? r.area_m2 + ' m²' : '–'}`}
                    fill="#fff"
                    fontSize={11 / zoom}
                    fontStyle="bold"
                    align="center"
                    verticalAlign="middle"
                    padding={2 / zoom}
                  />
                </Group>
              </Group>
            );
          })}
        </Layer>

        {/* Calibration line — only render during active calibration.
            Once scale is set, the orange polygon disappears from the canvas
            (the "Scale set" indicator in the toolbar makes the state explicit). */}
        <Layer listening={false}>
          {mode === 'calibrate' && calibrationLine && calibrationLine.length >= 2 && (
            <>
              <Line
                points={flattenPoints(calibrationLine)}
                stroke="#F59E0B"
                strokeWidth={3 / zoom}
                dash={[8 / zoom, 4 / zoom]}
              />
              {calibrationLine.map(([x, y], i) => (
                <Circle key={i} x={x} y={y} radius={5 / zoom} fill="#F59E0B" stroke="#fff" strokeWidth={1.5 / zoom} />
              ))}
            </>
          )}
          {mode === 'calibrate' && pendingCalib && (
            <>
              <Circle x={pendingCalib[0]} y={pendingCalib[1]} radius={5 / zoom} fill="#F59E0B" stroke="#fff" strokeWidth={1.5 / zoom} />
              {hover && (
                <Line
                  points={[pendingCalib[0], pendingCalib[1], hover[0], hover[1]]}
                  stroke="#F59E0B"
                  strokeWidth={2 / zoom}
                  dash={[6 / zoom, 4 / zoom]}
                />
              )}
            </>
          )}
        </Layer>

        {/* In-progress polygon (draw mode) */}
        <Layer listening={false}>
          {currentPolygon.length > 0 && (
            <>
              {/* rubber-band line to hover */}
              {mode === 'draw' && hover && (
                <Line
                  points={[
                    currentPolygon[currentPolygon.length - 1][0],
                    currentPolygon[currentPolygon.length - 1][1],
                    hover[0], hover[1],
                  ]}
                  stroke="#2563EB"
                  strokeWidth={2 / zoom}
                  dash={[6 / zoom, 4 / zoom]}
                />
              )}
              <Line
                points={flattenPoints(currentPolygon)}
                stroke="#2563EB"
                strokeWidth={2.5 / zoom}
                lineCap="round"
                lineJoin="round"
              />
              {currentPolygon.map(([x, y], i) => (
                <Circle
                  key={i}
                  x={x}
                  y={y}
                  radius={(i === 0 && snapActive ? 9 : 5) / zoom}
                  fill={i === 0 && snapActive ? '#16A34A' : '#2563EB'}
                  stroke="#fff"
                  strokeWidth={1.5 / zoom}
                />
              ))}
            </>
          )}
        </Layer>
      </Stage>

      {/* Top-left live overlay */}
      {livePolyInfo && (
        <div
          style={{
            position: 'absolute', top: 12, left: 12,
            background: 'rgba(15,39,66,.95)', color: '#fff',
            padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            pointerEvents: 'none', letterSpacing: '.02em',
          }}
          data-testid="live-polygon-measure"
        >
          {livePolyInfo.pts} points
          {livePolyInfo.areaM2 != null && ` · area ${livePolyInfo.areaM2} m²`}
          {livePolyInfo.perimeterM != null && ` · perimeter ${livePolyInfo.perimeterM} m`}
          {currentPolygon.length >= 3 && <span style={{ opacity: 0.85, fontWeight: 500 }}> · click start to close</span>}
        </div>
      )}

      {mode === 'calibrate' && pendingCalib && hover && (
        <div
          style={{
            position: 'absolute', top: 12, left: 12,
            background: 'rgba(245,158,11,.95)', color: '#fff',
            padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            pointerEvents: 'none',
          }}
          data-testid="live-calibration-measure"
        >
          Click second point · current length: {calibLivePx.toFixed(0)} px
        </div>
      )}

      {/* Bottom-right zoom readout */}
      <div
        style={{
          position: 'absolute', bottom: 10, right: 12,
          background: 'rgba(15,39,66,.8)', color: '#fff',
          padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
          pointerEvents: 'none',
        }}
      >
        {Math.round(zoom * 100)}%
      </div>

      {/* Magnifier overlay — appears on 350ms press-and-hold in draw/calibrate mode */}
      {magnifier && (
        <div
          data-testid="magnifier-overlay"
          style={{
            position: 'absolute',
            left: Math.max(8, Math.min(containerWidth - MAGNIFIER_SIZE - 8, magnifier.screen.x + MAGNIFIER_OFFSET.x)),
            top: Math.max(8, Math.min(containerHeight - MAGNIFIER_SIZE - 8, magnifier.screen.y + MAGNIFIER_OFFSET.y)),
            width: MAGNIFIER_SIZE, height: MAGNIFIER_SIZE,
            borderRadius: '50%',
            boxShadow: '0 6px 20px rgba(15,39,66,.35)',
            pointerEvents: 'none',
            zIndex: 10,
          }}
        >
          <canvas
            ref={magCanvasRef}
            width={MAGNIFIER_SIZE}
            height={MAGNIFIER_SIZE}
            style={{ display: 'block', borderRadius: '50%' }}
          />
        </div>
      )}
    </div>
  );
};

export default DrawingCanvas;
